import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const EditSeo = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const formRef = useRef();

    const [formData, setFormData] = useState({
        page_code: '',
        page_title: '',
        hero_section_heading: '',
        hero_section_description: '',
        hero_button_text: '',
        meta_description: '',
        meta_keywords: '',
        index: '',
        include_in_sitemap: ''
    });

    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? (checked ? 'true' : '') : value;
        setFormData((prev) => ({ ...prev, [name]: val }));
    };

    useEffect(() => {
        const $ = window.jQuery;

        $.validator.addMethod("ckeditorRequired", function (value, element) {
            const textOnly = value.replace(/<[^>]*>/g, '').trim();
            return textOnly.length > 0;
        }, "Description is required");

        if (formRef.current && $.fn.validate) {
            $(formRef.current).validate({
                ignore: ".ck-editor *",
                rules: {
                    page_code: { required: true },
                    page_title: { required: true },
                    hero_section_heading: { required: true },
                    hero_section_description: { required: true, ckeditorRequired: true },
                    hero_button_text: { required: true },
                    meta_description: { required: true, ckeditorRequired: true },
                    meta_keywords: { required: true }
                },
                messages: {
                    page_code: { required: "Please select page code" },
                    page_title: { required: "Please enter page title" },
                    hero_section_heading: { required: "Please enter hero section heading" },
                    hero_section_description: { required: "Please enter hero section description" },
                    hero_button_text: { required: "Please enter hero button text" },
                    meta_description: { required: "Please enter meta description" },
                    meta_keywords: { required: "Please enter meta keywords" }
                },
                errorElement: "label",
                errorClass: "is-invalid text-danger",
                validClass: "is-valid",
                highlight: (el, err, valid) => $(el).addClass(err).removeClass(valid),
                unhighlight: (el, err, valid) => $(el).removeClass(err).addClass(valid)
            });
        }

        const fetchSeo = async () => {
            try {
                const res = await api.get(`/seo/${id}`);
                if (res.status === 200 && res.data.seo) {
                    const seo = res.data.seo;
                    setFormData({
                        page_code: seo.page_code || '',
                        page_title: seo.page_title || '',
                        hero_section_heading: seo.hero_section_heading || '',
                        hero_section_description: seo.hero_section_description || '',
                        hero_button_text: seo.hero_button_text || '',
                        meta_description: seo.meta_description || '',
                        meta_keywords: seo.meta_keywords || '',
                        index: seo.index ? 'true' : '',
                        include_in_sitemap: seo.include_in_sitemap ? 'true' : ''
                    });
                }
            } catch (err) {
                handleErrors(err);
            }
        };

        fetchSeo();
    }, [id]);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            form.find('.is-valid, .is-invalid').removeClass('is-valid is-invalid');
            form.find('label.error, .invalid-feedback').remove();

            try {
                setSubmitting(true);
                const payload = {
                    ...formData,
                    index: formData.index === 'true' ? 1 : 0,
                    include_in_sitemap: formData.include_in_sitemap === 'true' ? 1 : 0
                };

                const res = await api.put(`/seo/${id}`, payload);
                if (res.status === 200) {
                    showToast("success", res.data.message, "Success");
                    navigate(ROUTES.SEO);
                }
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.SEO}>SEO</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to="#/">Edit SEO</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Edit SEO</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate>
                                <div className="row">

                                    <div className="mb-3 col-md-6">
                                        <label htmlFor="page_code">Page Code</label>
                                        <select
                                            className="form-select"
                                            name="page_code"
                                            id="page_code"
                                            value={formData.page_code}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Page</option>
                                            <option value="home">Home</option>
                                            <option value="about">About</option>
                                            <option value="contact">Contact</option>
                                            <option value="career">Career</option>
                                            <option value="project">Projects</option>
                                        </select>
                                    </div>

                                    {[
                                        { name: "page_title", label: "Page Title" },
                                        { name: "hero_section_heading", label: "Hero Section Heading" },
                                        { name: "hero_button_text", label: "Hero Button Text" },
                                        { name: "meta_keywords", label: "Meta Keywords" }
                                    ].map((field, index) => (
                                        <div className="mb-3 col-md-6" key={index}>
                                            <label htmlFor={field.name}>{field.label}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name={field.name}
                                                id={field.name}
                                                value={formData[field.name]}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    ))}

                                    <div className="mb-3 col-md-3">
                                        <label className="form-label d-block">Index</label>
                                        <div className="form-check form-switch form-check-custom form-check-solid">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="index"
                                                name="index"
                                                checked={formData.index === 'true'}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="index">
                                                Enable Indexing
                                            </label>
                                        </div>
                                    </div>

                                    <div className="mb-3 col-md-3">
                                        <label className="form-label d-block">Include in Sitemap</label>
                                        <div className="form-check form-switch form-check-custom form-check-solid">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="include_in_sitemap"
                                                name="include_in_sitemap"
                                                checked={formData.include_in_sitemap === 'true'}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="include_in_sitemap">
                                                Enable Sitemap
                                            </label>
                                        </div>
                                    </div>

                                    {[
                                        { name: 'meta_description', label: 'Meta Description' },
                                        { name: 'hero_section_description', label: 'Hero Section Description' }
                                    ].map(({ name, label }) => (
                                        <div key={name} className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                            <div className="mb-3 col-lg-12">
                                                <label className="form-label">{label}</label>
                                                <CKEditor
                                                    editor={ClassicEditor}
                                                    config={{
                                                        toolbar: [
                                                            'heading', '|',
                                                            'bold', 'italic', 'link',
                                                            '|', 'bulletedList', 'numberedList'
                                                        ]
                                                    }}
                                                    data={formData[name]}
                                                    onChange={(event, editor) => {
                                                        const data = editor.getData();
                                                        setFormData(prev => ({ ...prev, [name]: data }));
                                                        document.querySelector(`input[name="${name}"]`).value = data; // sync with hidden input
                                                    }}
                                                />
                                                <input
                                                    type="hidden"
                                                    name={name}
                                                    value={formData[name]}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                </div>

                                <div className="pt-3">
                                    <button
                                        type="button"
                                        onClick={handleFormSubmit}
                                        className="btn btn-sm btn-primary me-2"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="spinner-border spinner-border-sm me-1"></i>
                                                Submit
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-floppy-disk me-1"></i>
                                                Submit
                                            </>
                                        )}
                                    </button>
                                    <Link to={ROUTES.SEO} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditSeo;
